import { Injectable } from '@angular/core';

import * as _ from 'lodash';
import { from, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { FormValues } from '@alfresco/adf-core';
import {
  HistoricProcessInstanceQueryRepresentation,
  RestVariable,
  ResultListDataRepresentationTaskRepresentation,
  ProcessInstanceRepresentation,
} from '@alfresco/js-api';

import { Store } from '@ngrx/store';

import {
  ProcessInfoModel,
  CancelProcessModel,
  CancelProcessInstanceResponse,
  ProcessPrimaryFilterModel,
  ProcessPrimaryFilterCard,
  ProcessPrimaryFilterParams,
} from '../models/process-info.model';
import { ProcessInstanceInfoModel } from '../models/process-instance-info.model';
import { TaskGroupInfoModel } from '../models';

import { WkAlfrescoApiService } from '../../../services/wk-alfresco-api.service';

import { currentSiteSelector } from '../../../store/selectors/app.custom-selectors';
import { AppStore } from '../../../store/states/app.state';

@Injectable({
  providedIn: 'root',
})
export class ProcessListService {
  baseApi = 'activiti-app/api/enterprise';
  apiList = {
    taskListApi: `${this.baseApi}/historic-tasks/query`,
    processListApi: `${this.baseApi}/wk/process-instances-info`,
    processDefInfoApi: `${this.baseApi}/wk/process-definitions-info`,
    processInstanceApi: `${this.baseApi}/process-instances`,
    processInstanceWkApi: `${this.baseApi}/wk/process-instances`,
    processTemplateDiagramApi: `${this.baseApi}/wk/process-template-diagram`,
    completedProcessInstanceApi: `${this.baseApi}/wk/completed-process-diagram`,
    cancelProcessApi: `${this.baseApi}/wk/cancel`,
    groupAssigneeApi: `${this.baseApi}/wk/tasks`,
    primaryfiltersApi: `${this.baseApi}/wk/filters/processes/counts`,
    taskInfoApi: `${this.baseApi}/tasks`,
    processListGetUserAPi: `${this.baseApi}/users`,
  };
  acceptContentTypes = ['application/json'];
  sortingParam = {
    processTemplateName: 'processName',
    startDate: 'processStart',
    stepStartDate: 'stepStart',
    currentStep: 'stepName',
    timeTaken: 'timeTaken',
    assignee: 'stepAssignee',
    endDate: 'processEnd',
  };
  defaultFilter = 'IN PROGRESS';
  defaultProcessDefSort = 'name_asc';
  selectedTaskEntry: ProcessInstanceInfoModel;
  filterParams: ProcessPrimaryFilterParams[] = [
    {
      param: 'getActive',
      label: 'IN PROGRESS',
    },
    {
      param: 'getInactive',
      label: 'COMPLETED',
    },
    {
      param: 'getCanceled',
      label: 'CANCELLED',
    },
  ];

  onFileClicked = new Subject<string>();
  public currentStepAssigneeId = 0;
  public processId = 0;
  public attachmentName = '';
  constructor(
    private readonly apiService: WkAlfrescoApiService,
    private readonly store: Store<AppStore>
  ) {}

  getTaskData(
    processInstanceId: string
  ): Observable<ResultListDataRepresentationTaskRepresentation> {
    const taskQueryParam = {
      processInstanceId,
      finished: 'false',
    };

    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.apiList.taskListApi,
        httpMethod: 'POST',
        bodyParam: taskQueryParam,
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
    );
  }

  getQueryParamForProcessQuery(
    size: number,
    start = 0
  ): HistoricProcessInstanceQueryRepresentation {
    const queryParam = new HistoricProcessInstanceQueryRepresentation();
    queryParam.size = size;
    queryParam.start = start;
    return queryParam;
  }

  // test it to add pathParams
  getProcessData(
    size: number,
    sorting: any,
    start = 0,
    activeFilter = this.defaultFilter,
    filterApplied = false
  ): Observable<ProcessInfoModel> {
    return this.store.select(currentSiteSelector).pipe(
      filter((currentSite) => currentSite !== '' && currentSite !== 'no-site'),
      take(1),
      switchMap((currentSite: string) => {
        const sort = this.getSortParam(sorting);
        const filterParams = this.getFilterParam(activeFilter);
        let queryParams: any = {
          start,
          size,
          sort,
          siteId: currentSite,
        };
        if (filterApplied) {
          queryParams = {
            ...queryParams,
            filter: filterApplied,
            ...(this.currentStepAssigneeId > 0 && {
              currentStepAssigneeId: this.currentStepAssigneeId,
            }),
            ...(this.processId > 0 && { processId: this.processId }),
            ...(this.attachmentName?.length && {
              attachmentName: this.attachmentName,
            }),
          };
        }
        return from(
          this.apiService.callProcessClientCustomApi({
            path: `${this.apiList.processListApi}?${filterParams}`,
            httpMethod: 'GET',
            queryParams,
            contentTypes: this.acceptContentTypes,
            accepts: this.acceptContentTypes,
          })
        );
      })
    );
  }

  getSortParam(sorting: any) {
    const sortKey = this.sortingParam[sorting.key];
    const sortDir = sorting.direction === 'desc' ? 'dsc' : sorting.direction;
    return `${sortKey}_${sortDir}`;
  }

  getFilterParam(activeFilter: string): string {
    return this.filterParams
      .map((item) => `${item.param}=${item.label === activeFilter}`)
      .join('&');
  }

  getProcessInstanceDiagram(processInstanceId: string): Observable<Blob> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: `${this.apiList.processInstanceWkApi}/${processInstanceId}/diagram`,
        httpMethod: 'GET',
        contentTypes: [],
        accepts: ['*/*'],
        responseType: 'blob',
      })
    );
  }

  getCompletedProcessInstanceDiagram(
    processInstanceId: string
  ): Observable<Blob> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: `${this.apiList.completedProcessInstanceApi}`,
        httpMethod: 'GET',
        queryParams: {
          processDefinitionId: processInstanceId,
        },
        contentTypes: [],
        accepts: ['*/*'],
        responseType: 'blob',
      })
    );
  }

  getProcessTemplateDiagram(processDefinitionId: string): Observable<Blob> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.apiList.processTemplateDiagramApi,
        httpMethod: 'GET',
        queryParams: {
          processDefinitionId,
        },
        contentTypes: [],
        accepts: ['*/*'],
        responseType: 'blob',
      })
    );
  }

  getProcessDefinitions(
    sort: string = this.defaultProcessDefSort
  ): Observable<any> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.apiList.processDefInfoApi,
        httpMethod: 'GET',
        queryParams: {
          sort,
        },
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
    ).pipe(map((list: any) => list.data));
  }

  cancelProcess(
    cancelProcessQueryParam: Array<CancelProcessModel>
  ): Observable<CancelProcessInstanceResponse> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: this.apiList.cancelProcessApi,
        httpMethod: 'POST',
        bodyParam: cancelProcessQueryParam,
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
    );
  }

  public setSelectedTaskEntry(taskEntry: ProcessInstanceInfoModel): void {
    this.selectedTaskEntry = taskEntry;
  }

  getSelectedTaskEntry() {
    return this.selectedTaskEntry;
  }

  public getGroupAssignee(userId: number): Observable<TaskGroupInfoModel> {
    let taskId: string;
    if (this.selectedTaskEntry.tasks?.length) {
      taskId = this.selectedTaskEntry.tasks[0].id;
    } else {
      taskId = this.selectedTaskEntry.id;
    }
    const api = `${this.apiList.groupAssigneeApi}/${taskId}/users/${userId}/groups`;
    return from(
      this.apiService.callProcessClientCustomApi({
        path: api,
        httpMethod: 'GET',
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
    );
  }

  getFilterData(): Observable<ProcessPrimaryFilterCard[]> {
    return this.store.select(currentSiteSelector).pipe(
      filter((currentSite) => currentSite !== '' && currentSite !== 'no-site'),
      take(1),
      switchMap((currentSite: string) =>
        from(
          this.apiService.callProcessClientCustomApi({
            path: this.apiList.primaryfiltersApi,
            httpMethod: 'GET',
            queryParams: {
              siteId: currentSite,
            },
            contentTypes: this.acceptContentTypes,
            accepts: this.acceptContentTypes,
          })
        ).pipe(
          map((data: ProcessPrimaryFilterModel) => [
            { label: this.defaultFilter, value: data.activeCount },
            { label: 'COMPLETED', value: data.completedCount },
            { label: 'CANCELLED', value: data.canceledCount },
          ])
        )
      )
    );
  }

  setProcessInstanceVariables(
    processInstanceId: string,
    variables: RestVariable[]
  ): Observable<any> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: `${this.apiList.processInstanceApi}/${processInstanceId}/variables`,
        httpMethod: 'POST',
        bodyParam: variables,
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
    );
  }

  getTaskInfo(taskId: string): Promise<any> {
    return this.apiService.callProcessClientCustomApi({
      path: `${this.apiList.taskInfoApi}/${taskId}`,
      httpMethod: 'GET',
      contentTypes: this.acceptContentTypes,
      accepts: this.acceptContentTypes,
    });
  }

  getProcessStartInfo(processInstanceId: string): Promise<any> {
    return this.apiService
      .callProcessClientCustomApi({
        path: `${this.apiList.processInstanceApi}/${processInstanceId}`,
        httpMethod: 'GET',
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
      .then((processInstance: any) => processInstance.startedBy);
  }

  public getUserInfo(username: string): Observable<any> {
    return from(
      this.apiService.callProcessClientCustomApi({
        path: `${this.apiList.processListGetUserAPi}?filter=${username}`,
        httpMethod: 'GET',
        contentTypes: this.acceptContentTypes,
        accepts: this.acceptContentTypes,
      })
    );
  }

  public hasValidFilter(processFilterFormData): boolean {
    const filterData = processFilterFormData || {};
    const { attachmentName, processId, userMailId } = filterData;

    return [attachmentName, processId, userMailId].some(
      (value) => value !== undefined && value !== null && value !== ''
    );
  }

  public resetFilters(): void {
    this.currentStepAssigneeId = 0;
    this.processId = 0;
    this.attachmentName = '';
  }

  /**
   * Function in charge of call to new API for multiple process instances
   *
   * @param processDefinitionId (string) ID of the process
   * @param name (String) Name of the process
   * @param values (FormValues) values from the dialog form
   * @returns (Observable<ProcessInstanceRepresentation>) result of the call
   */
  startProcessWithMultipleInstances(
    processDefinitionId: string,
    name: string,
    values?: FormValues
  ): Observable<ProcessInstanceRepresentation> {
    return this.store.select(currentSiteSelector).pipe(
      filter((currentSite) => currentSite !== '' && currentSite !== 'no-site'),
      take(1),
      switchMap((currentSite: string) =>
        from(
          this.apiService.callProcessClientCustomApi({
            path: `${this.apiList.processInstanceWkApi}/${currentSite}`,
            httpMethod: 'POST',
            bodyParam: {
              name,
              processDefinitionId,
              values,
            },
            contentTypes: this.acceptContentTypes,
            accepts: this.acceptContentTypes,
          })
        )
      )
    );
  }
}
